import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastService } from 'angular-toastify';
import { PssLoadingComponent } from '../../../../../tools/template/pss/pss-loading/pss-loading.component';
import { ParkingFeeGroupListRow } from '../../../../../core/models/finance/master-fee.model';
import { MapperMasterFeeService } from '../../../../../core/mappers/finance/mapper-master-fee.service';
import { ApiMasterFeeService } from '../../../../../core/services/api/finance/api-master-fee.service';
import { MenuPermissionService } from '../../../../../core/services/menu-permission.service';
import { ModalParkingFeeGroupComponent } from '../../../modals/finance/modal-parking-fee-group/modal-parking-fee-group.component';

@Component({
  selector: 'app-manage-fee-group',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent, ModalParkingFeeGroupComponent],
  templateUrl: './manage-fee-group.component.html',
  styleUrl: './manage-fee-group.component.css',
})
export class ManageFeeGroupComponent implements OnInit {
  private readonly api = inject(ApiMasterFeeService);
  private readonly mapper = inject(MapperMasterFeeService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);

  items = signal<ParkingFeeGroupListRow[]>([]);
  isLoading = signal(false);
  searchText = signal('');
  currentPage = signal(1);
  pageSize = signal(20);
  totalItems = signal(0);
  totalPages = signal(1);

  isModalOpen = signal(false);
  selectedItem = signal<ParkingFeeGroupListRow | null>(null);

  get canCreate(): boolean {
    return this.permissionService.canCreate(this.router.url);
  }

  get canUpdate(): boolean {
    return this.permissionService.canUpdate(this.router.url);
  }

  ngOnInit(): void {
    this.loadTable();
  }

  get pagesArray(): number[] {
    const total = this.totalPages();
    const maxVisible = 5;
    let start = Math.max(1, this.currentPage() - 2);
    let end = Math.min(total, start + maxVisible - 1);
    if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }

  onRefresh(): void {
    this.currentPage.set(1);
    this.loadTable();
  }

  onSearch(): void {
    this.currentPage.set(1);
    this.loadTable();
  }

  setPage(page: number): void {
    if (page >= 1 && page <= this.totalPages()) {
      this.currentPage.set(page);
      this.loadTable();
    }
  }

  openCreate(): void {
    if (!this.canCreate) {
      this.toast.error('ไม่มีสิทธิ์สร้าง');
      return;
    }
    this.selectedItem.set(null);
    this.isModalOpen.set(true);
  }

  openEdit(item: ParkingFeeGroupListRow): void {
    if (!this.canUpdate) {
      this.toast.error('ไม่มีสิทธิ์แก้ไข');
      return;
    }
    this.selectedItem.set(item);
    this.isModalOpen.set(true);
  }

  closeModal(): void {
    this.isModalOpen.set(false);
    this.selectedItem.set(null);
  }

  onSaved(): void {
    this.closeModal();
    this.loadTable();
  }

  formatDate(value?: string | null): string {
    if (!value) return '-';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString('th-TH');
  }

  private loadTable(): void {
    this.isLoading.set(true);
    this.api.getParkingFeeGroupForTable(this.currentPage(), this.pageSize(), this.searchText()).subscribe({
      next: (res) => {
        const mapped = this.mapper.mapFeeGroupTable(res);
        this.items.set(mapped.items);
        this.totalItems.set(mapped.total);
        this.totalPages.set(mapped.totalPages || Math.max(1, Math.ceil(mapped.total / this.pageSize())));
        this.isLoading.set(false);
      },
      error: () => {
        this.items.set([]);
        this.isLoading.set(false);
        this.toast.error('โหลดข้อมูลกลุ่ม Fee ล้มเหลว');
      },
    });
  }
}
